// rc128 — Catálogo de secciones de Ajustes (portada de tarjetas + página por
// sección). Una sola fuente de verdad: slug (URL), título, descripción, icono,
// grupo y el componente que renderiza la sección.
import type { ComponentType } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Settings, Building2, Layout, Wallet, CreditCard, KeyRound, Database,
  ShieldCheck, Key as KeyIcon, Cable, Bell, Smartphone,
} from "lucide-react";
import { DatosIspSection } from "./DatosIspSection";
import { EmpresasSection } from "./EmpresasSection";
import { PlantillasSection } from "./PlantillasSection";
import { ConfigFacturacionSection } from "./ConfigFacturacionSection";
import { PagosWrapper } from "./PagosWrapper";
import { ConfigOvpnSection } from "./ConfigOvpnSection";
import { BackupsSection } from "./BackupsSection";
import { LicenciaSection } from "./LicenciaSection";
import { ApiKeysSection } from "./ApiKeysSection";
import { IntegracionesSection } from "./IntegracionesSection";
import { NotificacionesSection } from "./NotificacionesSection";
import { PortalClienteSection } from "./PortalClienteSection";

export type AjustesGrupo = "negocio" | "operacion" | "conectividad" | "sistema";

export const GRUPOS: { id: AjustesGrupo; titulo: string; descripcion: string }[] = [
  { id: "negocio", titulo: "Negocio", descripcion: "Quién eres y cómo te presentas ante el cliente." },
  { id: "operacion", titulo: "Operación", descripcion: "Facturación, cobros y avisos del día a día." },
  { id: "conectividad", titulo: "Conectividad", descripcion: "Accesos y sistemas con los que ByteISP se habla." },
  { id: "sistema", titulo: "Sistema", descripcion: "Respaldo y licencia de la plataforma." },
];

export type AjustesSeccion = {
  slug: string;
  titulo: string;
  descripcion: string;
  icon: LucideIcon;
  grupo: AjustesGrupo;
  component: ComponentType;
  /** palabras extra para el buscador */
  keywords?: string[];
  /** permiso requerido para ver la tarjeta/página (además de usuarios.read de la ruta) */
  perm?: string;
};

export const SECCIONES: AjustesSeccion[] = [
  { slug: "isp", titulo: "Datos del ISP", descripcion: "Nombre comercial, razón social, contacto y logo que salen en documentos.",
    icon: Settings, grupo: "negocio", component: DatosIspSection, keywords: ["nombre", "rfc", "logo", "contacto"] },
  { slug: "empresas", titulo: "Empresas", descripcion: "Sub-empresas y su cuenta de cobro Toku (Account ID + Account Key).",
    icon: Building2, grupo: "negocio", component: EmpresasSection, keywords: ["toku", "account key", "razón social", "cuenta de cobro", "multi-empresa"] },
  { slug: "portal", titulo: "Portal del cliente", descripcion: "Lo que ve el cliente en mi.: sesión, soporte, contacto y datos genéricos de pago.",
    icon: Smartphone, grupo: "negocio", component: PortalClienteSection, keywords: ["portal", "cliente", "sesión", "sla", "oxxo", "contacto", "soporte"] },
  { slug: "plantillas", titulo: "Plantillas", descripcion: "HTML de contrato, factura y recibo de pago que generan los PDFs.",
    icon: Layout, grupo: "negocio", component: PlantillasSection, keywords: ["contrato", "factura", "recibo", "pdf", "jinja"] },
  { slug: "facturacion", titulo: "Facturación y cobranza", descripcion: "Ciclo automático, días de gracia, hora de corte y promesas de pago.",
    icon: Wallet, grupo: "operacion", component: ConfigFacturacionSection, keywords: ["promesas", "corte", "gracia", "ciclo", "cfdi", "poller", "pausa", "migración"] },
  { slug: "pagos", titulo: "Pagos (Toku)", descripcion: "Pasarela, ambiente, webhook, excepciones y auditoría de sincronización.",
    icon: CreditCard, grupo: "operacion", component: PagosWrapper, keywords: ["toku", "webhook", "excepciones", "clabe", "spei"] },
  { slug: "notificaciones", titulo: "Notificaciones", descripcion: "Qué avisos recibe el equipo y por dónde.",
    icon: Bell, grupo: "operacion", component: NotificacionesSection, keywords: ["push", "alertas", "avisos"] },
  { slug: "ovpn", titulo: "VPN", descripcion: "Servidor OpenVPN para administrar los routers de la red.",
    icon: KeyRound, grupo: "conectividad", component: ConfigOvpnSection, keywords: ["openvpn", "routers", "túnel", "mikrotik"] },
  { slug: "integraciones", titulo: "Integraciones", descripcion: "ByteOLT, ByteACS y ByteNMS: la costura con el resto del ecosistema.",
    icon: Cable, grupo: "conectividad", component: IntegracionesSection, keywords: ["byteolt", "byteacs", "bytenms", "olt", "acs", "onu"] },
  { slug: "api-keys", titulo: "API Keys", descripcion: "Llaves para que otros sistemas consulten o escriban en ByteISP.",
    icon: KeyIcon, grupo: "conectividad", component: ApiKeysSection, keywords: ["api", "llaves", "integración", "scopes"] },
  { slug: "backups", titulo: "Backups", descripcion: "Respaldo diario cifrado de la base de datos y descarga manual.",
    icon: Database, grupo: "sistema", component: BackupsSection, keywords: ["respaldo", "postgres", "age", "restaurar"], perm: "backups.read" },
  { slug: "licencia", titulo: "Licencia", descripcion: "Estado de la licencia y conexión con ByteControl.",
    icon: ShieldCheck, grupo: "sistema", component: LicenciaSection, keywords: ["daso", "bytecontrol", "plan", "activar"] },
];

export function seccionPorSlug(slug: string | undefined): AjustesSeccion | undefined {
  return SECCIONES.find((s) => s.slug === slug);
}

// Mapa de anclas legacy (/ajustes#ancla) → sección. Antes cambiaban de pestaña.
export const HASH_A_SECCION: Record<string, string> = {
  "auto-facturacion": "facturacion",
};
